// Import the new certificate generator
import {
  downloadGeneratedCertificate,
  CertificateConfig,
  DEFAULT_CERTIFICATE_TEMPLATE,
  DEFAULT_CERTIFICATE_CBT_TEMPLATE,
} from './certificateGenerator'
import { postCertificateDownloadAnalytics } from '../api/user'

export function rupiahFormatter(
  value: number | string,
  withPrefix = true,
): string {
  const number = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(number)) return withPrefix ? 'Rp0' : '0'

  return `${withPrefix ? 'Rp' : ''}${number
    .toFixed(0)
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
}

export const calculateDiscount = (
  originalPrice: number,
  discount: string,
  discountType: 'PERCENTAGE' | 'NOMINAL',
) => {
  let priceAfterPercentageDiscount = originalPrice
  const discountNominal = parseInt(discount)

  if (discountType === 'PERCENTAGE') {
    let percentageDiscountAmount = 0
    percentageDiscountAmount = originalPrice * (discountNominal / 100)
    priceAfterPercentageDiscount = originalPrice - percentageDiscountAmount
  } else if (discountType === 'NOMINAL') {
    priceAfterPercentageDiscount = originalPrice - discountNominal
  } else {
    priceAfterPercentageDiscount = originalPrice
  }

  const totalDiscountAmount = rupiahFormatter(priceAfterPercentageDiscount)

  return totalDiscountAmount
}

/**
 * Download certificate helper function
 * @param certificateName - Name of the certificate
 * @param customFileName - Optional custom filename for the certificate
 * @param pdfUrl - Optional custom PDF URL, defaults to sample certificate
 * @param userName - Optional user name for auto-generated certificate
 * @param courseTitle - Optional course title for auto-generated certificate
 * @param useAutoGeneration - Whether to use auto-generated certificate instead of static PDF
 * @param courseId - Optional course ID for analytics tracking
 * @param courseTopics - Optional course topics to display in certificate
 */

export const downloadCertificate = async (
  certificateName: string,
  customFileName?: string,
  pdfUrl: string = '/assets/certificates/sample-certificate.pdf',
  userName?: string,
  courseTitle?: string,
  useAutoGeneration: boolean = false,
  courseId?: number,
  courseTopics?: Array<{ name: string }>,
  isCBT: boolean = false,
  cbtScore: string = "",
  cbtPassingGrade: string = ""
): Promise<void> => {
  try {
    // If auto-generation is enabled and we have user name and course title
    if (useAutoGeneration && userName && courseTitle) {
      const customConfig: Partial<CertificateConfig> = {
        ...(isCBT && {
          cbtScore: cbtScore,
          cbtScoreX: 340,
          cbtScoreY: 384,
          cbtIsPassed: Number(cbtScore) >= Number(cbtPassingGrade),
          cbtPassingGrade: cbtPassingGrade,
          cbtPassingGradeX: 392,
          cbtPassingGradeY: 471,
        }),
      }

      await downloadCertificateAuto(
        userName,
        courseTitle,
        customFileName,
        customConfig,
        courseTopics,
        isCBT
      )
    } else {
      // Fallback to static PDF download
      const link = document.createElement('a')
      link.href = pdfUrl

      // Generate filename with current date (date only, no day name)
      const currentDate = new Date().toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
      const fileName =
        customFileName || `certificate-${certificateName}-${currentDate}.pdf`

      link.download = fileName
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      console.log('Certificate download initiated:', fileName)
    }

    // Call analytics API after successful download
    if (courseId) {
      try {
        await postCertificateDownloadAnalytics({
          course_id: courseId,
          name: userName || certificateName,
        })
        console.log(
          'Certificate download analytics recorded for course:',
          courseId,
        )
      } catch (analyticsError) {
        console.error(
          'Failed to record certificate download analytics:',
          analyticsError,
        )
        // Don't throw error for analytics failure - download should still succeed
      }
    }
  } catch (error) {
    console.error('Error downloading certificate:', error)
    // You can add toast notification here if needed
  }
}

/**
 * Download certificate with custom name and position
 * @param config Certificate configuration with name and position
 * @param customFileName Optional custom filename
 */
export const downloadCertificateWithPosition = async (
  config: CertificateConfig,
  customFileName?: string,
): Promise<void> => {
  try {
    await downloadGeneratedCertificate(
      config,
      DEFAULT_CERTIFICATE_TEMPLATE,
      customFileName,
    )
  } catch (error) {
    console.error('Error downloading certificate with position:', error)
    throw error
  }
}

/**
 * Download certificate with auto-generated config from user name and course title
 * @param userName - Name of the user/student
 * @param courseTitle - Title of the course
 * @param customFileName - Optional custom filename for the certificate
 * @param customConfig - Optional custom configuration to override defaults
 * @param courseTopics - Optional course topics to display in certificate
 */
export const downloadCertificateAuto = async (
  userName: string,
  courseTitle: string,
  customFileName?: string,
  customConfig?: Partial<CertificateConfig>,
  courseTopics?: Array<{ name: string }>,
  isCBT: boolean = false
): Promise<void> => {
  try {
    console.log('Kena Disini Oke --> ', courseTitle);
    
    // Create config with auto-generated values
    const config: CertificateConfig = {
      name: userName,
      courseName: courseTitle,
      // Use default positions and styling
      y: isCBT ? 200 : 260, // Default Y position for name
      courseY: isCBT ? 300 : 360, // Default Y position for course (matching template)
      fontSize: 24, // Default font size
      fontColor: { r: 42, g: 170, b: 250 }, // Default blue color
      courseFontSize: 24, // Default course font size
      courseFontColor: { r: 42, g: 170, b: 250 }, // Default blue color
      // Enable text wrapping by default
      enableTextWrapping: true,
      maxTextWidth: 90,
      lineHeight: 1.3, // Line height multiplier
      // Add course topics if provided
      topics: courseTopics,
      // Add signature configuration by default
      signature: {
        signatureImageUrl: '/assets/certificates/assets ttd.png', // Default signature image
        location: 'Bandar Lampung',
        marginBottom: 64,
        fontSize: 12,
        fontColor: { r: 0, g: 0, b: 0 }, // Black color
        imageWidth: 200,
        imageHeight: 100,
      },
      // Override with custom config if provided
      ...customConfig,
    }

    // Generate filename with current date (date only, no day name)
    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const fileName =
      customFileName ||
      `certificate-${userName}-${courseTitle}-${currentDate}.pdf`

    if (isCBT) {
      await downloadGeneratedCertificate(
        config,
        DEFAULT_CERTIFICATE_CBT_TEMPLATE,
        fileName,
      )
    } else {
      await downloadGeneratedCertificate(
        config,
        DEFAULT_CERTIFICATE_TEMPLATE,
        fileName,
      )
    }

    console.log('Auto certificate download initiated:', fileName)
  } catch (error) {
    console.error('Error downloading auto certificate:', error)
    throw error
  }
}
